// REST layer. Mirrors the Flutter app exactly: plain JSON POSTs to the same
// backend (no auth header — the server's encryptedAuth middleware is pass-through,
// just like the Flutter client relies on).

import axios from 'axios';
import { localFirst, localFirstOne } from './localFirst';
import * as local from '../db/localRepo';
import { mergeRoomDevices } from '../devices/roomMerge';
import { fixFavouriteNames } from '../devices/favouriteNames';
import { isBrowseAll } from '../state/browseAll';

export const API_BASE =
  process.env.REACT_APP_API_URL || 'http://smartifyindia.co.in:8080/api';

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
});

// `config` is passed straight to axios, so a caller can override the default
// timeout for an endpoint known to be slow.
// Exported so feature modules can add their own endpoints without every call
// having to live in this file — same axios instance, same base URL, same shape.
export const post = (endpoint, body = {}, config) => api.post(`/${endpoint}`, body, config);

// ---------------- Auth / user ----------------
export const login = (mobileNumber, password) =>
  post('GetUserDetailsbyMobileNumberAndPassword', {
    MobileNumber: mobileNumber,
    Password: password,
  });

// ServiceBy must match the Flutter client exactly — the backend uses it as the
// sender name in the WhatsApp template.
export const sendOtp = (mobileNumber) =>
  post('SendOtpOnWhatsAppbyMobileNumber', {
    MobileNumber: mobileNumber,
    ServiceBy: 'Smart Life India',
  });

export const registerUser = (payload) => post('AddNewUser', payload);

export const updateUser = (data, where) =>
  post('UpdateUsers', { data, where });

// ---------------- Companies (Super Admin) ----------------
export const getCompanies = () => post('GetDataFromCompanyInfo', {});
export const addCompany = (payload) => post('AddCompanyInfo', payload);
export const updateCompany = (data, where) =>
  post('UpdateCompanyInfo', { data, where });

// ---------------- Device Types (Super Admin catalog) ----------------
// The product/device-type catalog (homeautomation.DeviceType). The list query
// joins ImageData + OTABinFile, so a row carries more columns than the table —
// send ONLY DeviceType columns back on add/update (see DEVICE_TYPE_COLUMNS).
export const getDeviceTypes = () => post('GetAllDeviceTypeDetails', {});

// AddDeviceType upserts on DeviceName (body IS the row).
export const addDeviceType = (row) => post('AddDeviceType', row);

// UpdateDeviceType takes { data, where } like every other Update endpoint.
export const updateDeviceType = (data, where) =>
  post('UpdateDeviceType', { data, where });

// ---------------- OTA bin files (Super Admin) ----------------
// The firmware catalogue (homeautomation.OTABinFile) — one row per .bin, and
// what DeviceType.OTABinFileId points at. Written when a build is published;
// see core/devices/otaBinFiles.js, which also owns the file upload itself (that
// one is NOT an /api route, so it is not here).

// Two columns only — OTABinFileId + BinFileName. For the rest of a row, ask by
// name; there is no "get all, in full".
export const getOtaBinFiles = () => post('GetAllOTABinFile', {});

export const getOtaBinFileByName = (binFileName) =>
  post('GetOTABinFileByBinFileName', { BinFileName: binFileName });

// ⚠️ UPSERTS ON BinFileName, and the body IS the row. Re-publishing a build
// updates the existing row rather than adding a second one — which is why the
// filename is the identity of a firmware here, and why overwriting a name
// re-points every device already on it.
export const addOtaBinFile = (row) => post('AddOTABinFile', row);

// ⚠️ THE CALL THE FIRMWARE ITSELF MAKES, so its answer is the truth about what
// a device will download — not what any table says. The device takes the FIRST
// STRING FIELD of the first record whatever it is named, and requires it to
// start with `http` (OTA_CLAUDE.md §10). Used to CONFIRM a mapping rather than
// to assume it.
export const getDeviceOtaUrl = (uniqueNumber) =>
  post('GetDeviceOTAUrlLink', { UniqueNumber: uniqueNumber });

// ---------------- Locations ----------------
// LOCAL-FIRST from here down (structure only). On a native build these read the
// SQLite cache and only touch the network when it can't answer — that is what
// keeps a normal launch free of structure calls. Live status and control are
// deliberately NOT cached; they still go over MQTT/REST every time.
export const getLocations = (userId) => localFirst(
  () => local.getLocations(userId),
  () => post('GetLocationsByUserId', { UserId: userId }),
);

// EVERY location on the server, regardless of who owns it — 117 rows and no
// UserId in the request. Admin/testing only: it is what lets a Super Admin open
// a customer's site without being mapped to it.
//
// ⚠️ THE ROW IS MUCH FATTER THAN THE PER-USER ONE, not thinner — 33 columns
// against 3. GetLocationsByUserId returns only LocationName, LocationID and
// PlaceName; this one adds MasterUserId, the owner's Surname/Name/FatherHusband,
// WifiName/WifiPassword, ADD1/2/CITY/PINCODE, DevicesConnected and the rest of
// the Locations row. So code written against this list can read fields that are
// ABSENT in the normal one — the direction the risk actually runs.
//
// PlaceName is the single exception: it exists only on the per-user reply.
//
// LocationWithName is already "<owner>-<name>", which is why AppContext prefers
// it as the display name when this list is in use.
export const getAllLocations = () => post('GetAllLocations', {});

export const getLocationDetail = (locationId) =>
  post('GetLocationDetailsbyLocationID', { LocationID: locationId });

// ---------------- Rooms ----------------
//
// ⚠️ ALL THREE OF THESE ARE USER-SCOPED, and that is the whole reason
// core/state/browseAll exists. In the Super Admin's All Locations mode the
// location list covers the estate while these still ask "which of THIS user's
// rooms are in it" — for somebody else's house, none — and the screen reads
// "No rooms". Read that file before changing the branch.
//
// The local SQLite cache is skipped in that mode: it is built from the signed-in
// user's own sync, so it can only ever answer for them.

// ⚠️ THE UNFILTERED ENDPOINTS RETURN THINNER ROWS, and a screen that reads a
// column which is suddenly absent shows a blank where it used to show a value —
// trading one empty screen for another. Measured against the per-user replies:
//
//   rooms    lose  LocationID, SortOrder, LocationName, ADD1/2, CITY, PINCODE
//   devices  lose  RoomID, SortOrder, favourite, lastUpdatedDateTime, RoomName,
//                  Outputs, Inputs, ImageLocation, MqttDevice, ConnectedInMesh
//
// The ones that are load-bearing are filled in here, from what the CALLER
// already knows. The rest are genuinely absent and their consumers cope:
// DeviceStatusDot and useHideOffline both fall back to the status endpoint when
// a row carries no timestamp — the documented path for the lists that never had
// one — and Outputs/Inputs are only read by admin screens that fetch their own.
//
// favourite is defaulted to 0 on purpose rather than looked up: favourites are
// per-user, and an admin's own stars mean nothing on somebody else's devices.

const withRoomDefaults = (rows, roomId) => rows.map((d, i) => ({
  SortOrder: i,
  favourite: 0,
  ...d,
  RoomID: d.RoomID ?? roomId,
}));

/** Every room in the location, regardless of who is mapped to it. */
export const getAllRoomsAtLocation = async (locationId) => {
  const res = await post('GetRoomsByLocationID', { LocationID: locationId });
  const rows = Array.isArray(res.data) ? res.data : [];
  return {
    ...res,
    data: rows.map((r, i) => ({ SortOrder: i, ...r, LocationID: r.LocationID ?? locationId })),
  };
};

/** Every device in the room, regardless of mapping — and of the type filter. */
export const getAllDevicesInRoom = async (roomId) => {
  const res = await post('GetDevicesByRoomID', { RoomID: roomId });
  return { ...res, data: withRoomDefaults(Array.isArray(res.data) ? res.data : [], roomId) };
};

export const getRooms = (userId, locationId) => (isBrowseAll()
  ? getAllRoomsAtLocation(locationId)
  : localFirst(
    () => local.getRooms(userId, locationId),
    () => post('GetRoomsbyUserIDandLocationId', { UserId: userId, LocationID: locationId }),
  ));

// There is NO unfiltered common-devices endpoint — GetCommonDevicesByLocationID
// looks like one but still joins on UserId (probed: 4 rows for user 1, 0 for
// anyone else). So admin mode assembles the list from two calls that take no
// user: the location's devices, which name the roomless ones, and the RoomId 0
// device rows, which carry the DeviceID needed to draw a control.
//
// The RoomId 0 list is estate-wide (~1300 rows) and is therefore cross-
// referenced against THIS location's roomless unique numbers — without that
// every location would show every common device in the database.
async function allCommonDevicesAtLocation(locationId) {
  const [atLocation, roomZero] = await Promise.all([
    getDevicesByLocation(locationId).then((r) => (Array.isArray(r.data) ? r.data : [])),
    // Raw, not getAllDevicesInRoom: the defaults are applied AFTER filtering, so
    // SortOrder numbers this location's few rather than the estate's 1300.
    post('GetDevicesByRoomID', { RoomID: 0 }).then((r) => (Array.isArray(r.data) ? r.data : [])),
  ]);
  const roomless = new Set(
    atLocation.filter((d) => !d.RoomName).map((d) => String(d.UniqueNumber)),
  );
  const mine = roomZero.filter((d) => roomless.has(String(d.UniqueNumber)));
  // RoomId 0 is a REAL value meaning "belongs to the location" — not a missing
  // one — so it is set explicitly rather than left undefined.
  return { data: withRoomDefaults(mine, 0) };
}

export const getCommonDevices = (userId, locationId) => (isBrowseAll()
  ? allCommonDevicesAtLocation(locationId)
  : localFirst(
    () => local.getCommonDevices(locationId),
    () => post('GetCommonDevicesbyUserIDandLocationId', {
      UserId: userId,
      LocationID: locationId,
    }),
  ));

// This USER's devices at a location, each row carrying its RoomID. Used to
// complete the room list below — see core/devices/roomMerge.js for why one
// endpoint is not enough.
//
// ⚠️ Not to be confused with getDevicesByLocation further down: that one takes
// no UserId and answers for the whole location regardless of ownership, which
// would leak devices into a room list. This one is filtered by
// UserDeviceMapping server-side, like the room endpoint it completes.
export const getUserDevicesAtLocation = (userId, locationId) =>
  post('GetDeviceListsbyUserIDandLocationId', { UserId: userId, LocationID: locationId });

// The completion source is per LOCATION while the call is per ROOM, so a screen
// that loads eight rooms in parallel would fetch the same list eight times.
// Short-lived because it also carries Status: long enough to cover one screen's
// fan-out, far short of the 60 s poll that refreshes the online dots.
const LOCATION_DEVICES_TTL_MS = 10000;
const locationDevicesCache = new Map();

function locationDevicesOnce(userId, locationId) {
  const key = `${userId}:${locationId}`;
  const hit = locationDevicesCache.get(key);
  if (hit && Date.now() - hit.at < LOCATION_DEVICES_TTL_MS) return hit.rows;
  // Swallowed deliberately: this only ADDS devices, so a failure here must leave
  // the room as it was, never empty it.
  const rows = getUserDevicesAtLocation(userId, locationId)
    .then((res) => (Array.isArray(res.data) ? res.data : []))
    .catch(() => []);
  locationDevicesCache.set(key, { at: Date.now(), rows });
  return rows;
}

/**
 * A room's devices.
 *
 * ⚠️ PASS THE LOCATION. Without it this is the bare room endpoint, which drops
 * whole device types server-side — a curtain that is in the room, mapped to the
 * user and active still does not come back, and the app shows a room that is
 * missing a device nobody can explain. `locationId` is what lets the missing
 * ones be merged back in; see core/devices/roomMerge.js.
 *
 * The local cache needs none of it — it is built from the install table, which
 * has no such filter — so this only affects the REST path.
 */
/*
 * The types BOTH server queries exclude, fetched by name.
 *
 * ⚠️ THIS IS THE ONLY WAY TO SEE A DOOR LOCK, AN IR BLASTER OR AN
 * EXTERNAL-BUTTON SWITCH IN A ROOM. roomMerge documents two exclusion lists:
 *
 *   room     ... and a.DeviceId not in (2,13,3,4,19,20,21,26,30,36,45,47,51)
 *   location ... and a.DeviceId <> 3 and a.DeviceId <> 19 and a.DeviceId <> 21
 *
 * and 3, 19 and 21 are in BOTH — so merging the location list, which recovers
 * everything else, cannot reach them. That is why roomMerge's header says no
 * client-side merge can, and why the desktop grew a workaround that matches
 * devices by room+location NAME.
 *
 * ⚠️ That note is now out of date. `GetDeviceDetailsbyUserIdAndRoomIDAndDeviceID`
 * takes a SQL IN(...) list and returns exactly these rows, in the room-row
 * shape, filtered through UserDeviceMapping like every other list. Probed
 * 2026-08-08 (UserId 1, RoomID 346): the plain room query returns 11 rows and no
 * blaster; this one returns both blasters with LocationID/UserId/Id as well. The
 * Flutter repo names the same endpoint in a commented-out line
 * (repository.dart:1281-1283).
 *
 * Swallowed on failure — like the location fetch — because this only ADDS
 * devices, so an error must leave the room as it was rather than empty it.
 */
const DOUBLE_EXCLUDED_IDS = '3,19,21';

const devicesByRoomAndType = (userId, roomId) =>
  post('GetDeviceDetailsbyUserIdAndRoomIDAndDeviceID', {
    UserId: userId, RoomID: roomId, DeviceID: DOUBLE_EXCLUDED_IDS,
  })
    .then((r) => (Array.isArray(r.data) ? r.data : []))
    .catch(() => []);

export const getDevicesByRoom = (userId, roomId, locationId) => (isBrowseAll()
  // No merge needed: the unfiltered query has no type-exclusion clause, so the
  // devices roomMerge exists to recover are already in the reply.
  ? getAllDevicesInRoom(roomId)
  : localFirst(
    () => local.getRoomDevices(roomId),
    async () => {
      const [res, doubleExcluded] = await Promise.all([
        post('GetDeviceDetailsbyUserIdAndRoomId', { UserId: userId, RoomID: roomId }),
        devicesByRoomAndType(userId, roomId),
      ]);
      const rows = Array.isArray(res.data) ? res.data : [];
      const locationRows = (locationId == null || locationId === '')
        ? [] : await locationDevicesOnce(userId, locationId);
      // One merge over BOTH recovery sources — mergeRoomDevices dedupes by
      // unique number, so a device reachable from either is added once.
      const merged = mergeRoomDevices(rows, [...locationRows, ...doubleExcluded], roomId);
      return merged === rows ? res : { ...res, data: merged };
    },
  ));

// ---------------- Reorder (SortOrder) ----------------
// Locations + devices are per-user (mapping tables); rooms are global (Rooms table).
export const updateLocationSortOrder = (userId, locationId, sortOrder) =>
  post('UpdateUserLocationMappingbyUserIdAndLocationID', {
    data: { SortOrder: sortOrder },
    where: `UserId = ${userId} AND LocationID = ${locationId}`,
  });

export const updateRoomSortOrder = (roomId, sortOrder) =>
  post('UpdateRooms', {
    data: { SortOrder: sortOrder },
    where: `RoomID = ${roomId}`,
  });

// Rooms are GLOBAL, not per-user (Rooms.RoomName, no mapping involved) — so a
// rename is seen by everyone who has access to that room, not just the person
// doing it. Worth knowing before renaming someone else's site.
export const renameRoom = (roomId, roomName) =>
  post('UpdateRooms', {
    data: { RoomName: roomName },
    where: `RoomID = ${roomId}`,
  });

export const updateDeviceSortOrder = (userId, deviceInstalledId, sortOrder) =>
  post('UpdateUserDeviceMappingByUserIDAndDeviceInstalledID', {
    data: { SortOrder: sortOrder },
    where: `UserId = ${userId} AND DeviceInstalledID = ${deviceInstalledId}`,
  });

// ---------------- Devices ----------------
export const getDevicesByUser = (userId) => localFirst(
  () => local.getAllDevices(),
  () => post('GetListofDevicesbyUserId', { UserId: userId }),
);

// Every device at a location, REGARDLESS OF WHO OWNS IT — no UserId in the
// request. Deliberately not local-first: the cache only holds the signed-in
// user's devices, so serving from it would silently return a subset.
//
// For the Super Admin console, which works on any unique number on the broker.
// Returns a thin row: UniqueNumber, RoomName, DeviceName, DeviceTypeName,
// NetworkLastUpdatedDateTime, Connected.
export const getDevicesByLocation = (locationId) =>
  post('GetAllDevicesByLocationID', { LocationID: locationId });

/*
 * ⚠️ THIS REPLY NAMES ITS TWO NAME COLUMNS BACKWARDS — `DeviceName` holds the
 * product type and `ActualDeviceName` the customer's name, the opposite of every
 * other query here. Put right by core/devices/favouriteNames, which carries the
 * probe that proves it. Both the REST and the cached path go through it, because
 * the cache is built from this same endpoint.
 */
export const getFavouriteDevices = (userId) => localFirst(
  () => local.getFavouriteDevices(),
  () => post('GetListoffavouriteDevicesbyUserId', { UserId: userId }),
).then((res) => (Array.isArray(res && res.data)
  ? { ...res, data: fixFavouriteNames(res.data) }
  : res));

export const getDeviceDetail = (uniqueNumber) => localFirstOne(
  () => local.getDeviceDetail(uniqueNumber),
  () => post('GetDeviceDetailbyUniqueNumber', { UniqueNumber: uniqueNumber }),
);

// ⚠️ THE SERVER'S ROW, NEVER THE CACHE. getDeviceDetail above is local-first,
// and on a native build it can answer from SQLite — which is built from the
// signed-in user's own sync and therefore cannot know about a bench board that
// belongs to nobody, let alone its current OTABinFileId. Admin tools that are
// about to WRITE that column must read the real one first, or they will show
// the operator a stale "before" and a mapping decision made against it.
export const getDeviceDetailFresh = (uniqueNumber) =>
  post('GetDeviceDetailbyUniqueNumber', { UniqueNumber: uniqueNumber });

// ⚠️ THE ONLY REPLY THAT CARRIES `WifiName`, i.e. the network the device is
// actually joined to. `GetDeviceDetailbyUniqueNumber` names its columns
// (`a.ID, a.MasterUserId, … , b.*`) and WifiName is not among them, so the SSID
// is invisible there even though the firmware writes it on EVERY status update
// (device_api.c -> `UpdateStatusByUniqueNumber` with `data.WifiName`). This one
// is `select a.* from DevicesInstalled`, so it has the whole row.
//
// Takes the INSTALLED id (DevicesInstalled.ID), not the unique number.
//
// ⚠️ The row also contains `WifiPassword` in clear. Copy out the fields you
// need; never hand the whole row to anything that logs.
export const getDeviceInstalledById = (installedId) =>
  post('GetDeviceInstalledDetailsbyDevicesInstalledId', { ID: installedId });

export const getDeviceStatus = (uniqueNumber) =>
  post('GetDeviceStatusbyUniqueNumber', { UniqueNumber: uniqueNumber });

export const getOutputs = (uniqueNumber) => localFirst(
  () => local.getOutputs(uniqueNumber),
  () => post('GetOutputDetailbyUniqueNumber', { UniqueNumber: uniqueNumber }),
);

// Current-sense readings for a phase meter on a given day (YYYY-M-D).
// Returns rows { DateTime, current, power, phase } (~every 30-60s).
/*
 * ⚠️ THE DATE IS CONCATENATED STRAIGHT INTO SQL ON THE SERVER, so a bad one is
 * not a bad answer — it is a broken query. Confirmed against the live API: omit
 * this field, or send null, or send the string "undefined", and the server
 * builds
 *
 *     ... and ctse.DateTime >= 'undefined 00:00:00' and ctse.DateTime <= 'undefined 23:59:00'
 *
 * which MySQL rejects with "Incorrect DATETIME value". JSON cannot carry
 * `undefined`, so passing `dateStr === undefined` here DROPS THE KEY ENTIRELY
 * and produces exactly that.
 *
 * ⚠️ AND THE SERVER ANSWERS **HTTP 200** WITH AN ERROR BODY:
 *     {"status":"500","LineNumber":1530,"query":{...}}
 * so nothing downstream rejects. Every caller that trusts a 200 — which is all
 * of them, in this app and in both Flutter apps — reads a failure as success.
 * `core/devices/phase.fetchDay` is where that is caught for this endpoint.
 *
 * Validating here means a caller with a broken date fails loudly, in the caller,
 * instead of a server 500 that arrives dressed as an empty day.
 */
const DATE_PARAM_RE = /^\d{4}-\d{1,2}-\d{1,2}$/;

export const getCurrentSense = (uniqueNumber, dateStr) => {
  if (!uniqueNumber) {
    return Promise.reject(new Error('getCurrentSense: uniqueNumber is required'));
  }
  if (!DATE_PARAM_RE.test(String(dateStr))) {
    return Promise.reject(new Error(
      `getCurrentSense: DateTime must be YYYY-M-D — got ${JSON.stringify(dateStr)}. `
      + 'Sending it would produce invalid SQL on the server.',
    ));
  }
  return post('GetCurrentSenseByUniqueNumberAndDateOnly', {
    UniqueNumber: uniqueNumber,
    DateTime: dateStr,
  });
};

/*
 * ── The MQTT message log ───────────────────────────────────────────────────
 *
 * What the broker actually carried, stored server-side. Two shapes of the same
 * data: an aggregate per device, and the individual messages.
 *
 * ⚠️ `StartDateTime` / `EndDateTime`, NOT StartDate / EndDate. The short names
 * fail differently on the two endpoints — 400 on one, a 200-wrapped
 * `{"status":"500"}` on the other, which reads as an empty day.
 *
 * ⚠️ Logging is PER DEVICE and mostly off: `DevicesInstalled.AllowIncoming`
 * gates the device side and `SaveOutGoingMessages` the client side. Of user 1's
 * 203 devices, 39 and 1 respectively. A device missing from these replies is
 * usually not a quiet device — it is an unlogged one.
 *
 * See core/devices/messageStats for the 500-row cap on the list.
 */
export const getMqttMessageCounts = (range) =>
  post('GetMqttMessageCountForPeriodbyStartDateAndEndDate', range);

export const getMqttMessageList = (range) =>
  post('GetMqttMessageListForPeriodbyStartDateAndEndDate', range);

export const getButtonsByLocationAndUnique = (locationId, uniqueNumber) =>
  post('GetButtonsbyLocationIdAndUniqueNumber', {
    LocationID: locationId,
    UniqueNumber: uniqueNumber,
  });

export const updateOutputName = (outputName, uniqueNumber, output) =>
  post('UpdateOutputDetails', {
    data: { OutputName: outputName },
    where: `UniqueNumber = '${uniqueNumber}' AND Output = '${output}'`,
  });

// ---------------- Device settings (rename, output props) ----------------
export const updateDeviceName = (uniqueNumber, deviceName) =>
  post('UpdateDeviceInstalledByUniqueNumber', {
    data: { DeviceName: deviceName },
    UniqueNumber: uniqueNumber,
  });

// Generic per-output update ({ OutputName } now; button props later).
export const updateOutputDetail = (uniqueNumber, output, data) =>
  post('UpdateOutputDetailsByUniqueNumberAndOutput', {
    data,
    UniqueNumber: uniqueNumber,
    Output: output,
  });

export const setFavourite = (deviceInstalledId, userId, favourite) =>
  post('UpdateUserDeviceMapping', {
    data: { favourite: favourite ? 1 : 0 },
    where: `DeviceInstalledID = ${deviceInstalledId} AND UserId = ${userId}`,
  });

// ---------------- Schedules ----------------
export const getSchedules = (uniqueNumber, output) =>
  post('GetSchedulerListByUniqueNumberAndOutput', {
    UniqueNumber: uniqueNumber,
    Output: output,
  });

export const addSchedule = (payload) => post('AddSchedulerList', payload);

// ---------------- Two-way ("router") mappings ----------------
// Every mapping involving this device, EITHER side — a button can be the first
// or the second half of a pair, so callers must check both.
//
// ⚠️ The column is `FristButtonNumber` — the backend's typo, not ours. Spelling
// it correctly returns nothing.
export const getTwoWayMappings = (uniqueNumber) =>
  post('GetTwoWayAllButtonsDetailsByUniqueNumber', { UniqueNumber: uniqueNumber });

// ---------------- Device sharing / claiming ----------------
// Map an existing installed device to a user. Used both when accepting a shared
// device and when adding one manually by Unique Number.
// Note the string coercion — the Dart sends both as strings.
export const addUserDeviceMapping = (userId, deviceInstalledId) =>
  post('AddUserDeviceMapping', {
    UserId: String(userId),
    DeviceInstalledID: String(deviceInstalledId),
  });
export const updateSchedule = (data, where) =>
  post('UpdateSchedulerList', { data, where });
export const deleteSchedule = (where) =>
  post('DeleteSchedulerList', { where });

// ---------------- Wall Mode ----------------
export const getWallPanels = (userId) =>
  post('GetWallPanelsByUserId', { UserId: userId });
export const addWallPanel = (payload) => post('AddWallPanel', payload);
export const updateWallPanel = (data, where) =>
  post('UpdateWallPanel', { data, where });
export const deleteWallPanel = (where) => post('DeleteWallPanel', { where });

export const addWallButton = (payload) => post('AddWallButton', payload);
export const updateWallButton = (data, where) =>
  post('UpdateWallButton', { data, where });
export const deleteWallButton = (where) => post('DeleteWallButton', { where });

export const addWallButtonAction = (payload) =>
  post('AddWallButtonAction', payload);
export const deleteWallButtonActionsByButtonId = (wallButtonId) =>
  post('DeleteWallButtonActionsByWallButtonId', { WallButtonId: wallButtonId });

// ═══════════════════════════════════════════════════════════════════════════
// Endpoints ported from the Flutter app (SmartLifeIndia-flutter). Bodies match
// the Dart callers byte-for-byte — see docs/08-flutter-parity-plan.md.
// ═══════════════════════════════════════════════════════════════════════════

// ---------------- Profile image ----------------
// linker.dart getImageId() — resolves Users.ImageId / DevicesInstalled.ImageId
// to a URL. Returns rows [{ ImageUrl }].
export const getImageById = (imageId) =>
  post('GetImageDatabyImageId', { ImageId: imageId });

// ---------------- Locations (create / edit) ----------------
// locationFunctions.dart addLocation() → returns { insertId }.
export const addLocation = (payload) => post('AddLocation', payload);

export const addUserLocationMapping = (userId, locationId) =>
  post('AddUserLocationMapping', { UserId: userId, LocationID: locationId });

export const updateLocation = (locationId, data) =>
  post('UpdateLocation', { data, where: `LocationID='${locationId}'` });

// ---------------- Location WiFi ----------------
// The authoritative list of networks for a site — one row per SSID, with the
// password, shared by every device installed there.
//
// ⚠️ BROKEN ON THE SERVER as of 2026-07-28. The route exists (it does not 404)
// but NEVER SENDS A RESPONSE — verified against LocationID 29, which is real and
// has devices, and against ids that match nothing. Flutter calls the same
// endpoint (locationFunctions.dart getWiFis) and hangs identically, so this is
// not a porting difference. Every caller must therefore treat a failure as
// "unavailable", never as "this location has no networks".
//
// The explicit timeout is what turns an indefinite hang into a failure a caller
// can handle; without it the awaiting dialog spins forever.
// ── A location's known Wi-Fi networks ──────────────────────────────────────
//
// THREE endpoints exist and only two are usable. Verified against the live
// server 2026-08-01:
//
//   GetActiveWifiNetworksByLocationId   404 — in the Home Automation source but
//                                       NOT deployed. Best when it ships: it
//                                       joins on devices seen in the last N
//                                       minutes, so it drops the router that
//                                       was replaced last year, the firmware's
//                                       own 'Smartify'/'Home' fallbacks, and
//                                       rows with a blank password.
//   GetWifiNetworksbyLocationID         200 in ~30ms. What works today.
//   GetAllWifiNetworksByLocationId      HANGS. Never responds, for real ids and
//                                       bogus ones alike. This is what the app
//                                       used to call, which is why "saved for
//                                       this location" was always empty.
//
// Both working shapes return [{ WIFISSID, WIFIPASSWORD }] — note the CAPS; the
// old code mapped WifiName/WifiPassword and would have shown blanks even on a
// good reply.
export const getActiveLocationWifi = (locationId, minutes) =>
  post('GetActiveWifiNetworksByLocationId', {
    LocationID: locationId,
    ...(minutes ? { Minutes: minutes } : {}),
  }, { timeout: 8000 });

export const getLocationWifiList = (locationId) =>
  post('GetWifiNetworksbyLocationID', { LocationID: locationId }, { timeout: 8000 });

export const addLocationWifi = (locationId, ssid, password) =>
  post('AddWifiNameAndWifiPasswordByLocationId', {
    LocationID: locationId,
    SSID: ssid,
    Password: password,
  });

// ---------------- Rooms (create) ----------------
// roomScreenFunctions.dart addRoom() → returns { insertId }.
export const addRoom = (roomName, locationId) =>
  post('AddRooms', { RoomName: roomName, LocationID: Number(locationId) });

export const addLocationRoomsMapping = (userId, locationId, roomId) =>
  post('AddLocationRoomsMapping', {
    UserId: userId,
    LocationID: Number(locationId),
    RoomID: Number(roomId),
  });

// ---------------- Devices (generic install-row update) ----------------
// The Flutter app writes DevicesInstalled through UpdateDeviceInstalled with a
// { data, where } pair — used for rename, BodyColor and BodyType. Kept separate
// from updateDeviceName() above (which uses the ByUniqueNumber variant) so both
// wire formats stay available.
export const updateDeviceInstalled = (uniqueNumber, data) =>
  post('UpdateDeviceInstalled', {
    data,
    where: `UniqueNumber = '${uniqueNumber}'`,
  });

export const renameDeviceInstalled = (uniqueNumber, deviceName) =>
  updateDeviceInstalled(uniqueNumber, { DeviceName: deviceName });

// BodyColor: 0 Black · 1 White. BodyType: 0 Cona · 1 GM · 2 Goldmedal.
export const setDeviceBodyColor = (uniqueNumber, color) =>
  updateDeviceInstalled(uniqueNumber, { BodyColor: color });

export const setDeviceBodyType = (uniqueNumber, type) =>
  updateDeviceInstalled(uniqueNumber, { BodyType: type });

// ---------------- Remove a device ----------------
// homeScreenFunctions.dart removeDevice(): unassign the install row, then drop
// this user's mapping to it.
export const unassignDevice = (uniqueNumber) =>
  updateDeviceInstalled(uniqueNumber, { MasterUserId: 0 });

// ── The three mapping deletes ──────────────────────────────────────────────
//
// ⚠️ `where` GOES STRAIGHT INTO THE SQL. The backend's query builder is literally
//
//     { table: "UserDeviceMapping", where: req.where }
//
// with no validation, so this is `DELETE FROM <table> WHERE <whatever you sent>`.
// Two consequences worth stating rather than discovering:
//
//   • Every id is coerced with Number() below. A stray string would be
//     interpolated raw into a DELETE.
//   • A MISSING where does NOT wipe the table — it builds `WHERE undefined` and
//     the server answers 500. Verified against the live API.
//
// ⚠️ THE `affectedRows` IN THE REPLY IS NOT TO BE TRUSTED. These endpoints
// return counts belonging to other tables entirely — a delete matching nothing
// in UserLocationMapping (306 rows) reported 12,975, which is the size of
// UserDeviceMapping. Judge success by re-reading the list, never by the count.
const num = (v) => Number(v) || 0;

export const deleteUserDeviceMapping = (deviceInstalledId, userId) =>
  post('DeleteFromUserDeviceMapping', {
    where: `DeviceInstalledID = ${num(deviceInstalledId)} and UserId = ${num(userId)}`,
  });

export const deleteUserLocationMapping = (userId, locationId) =>
  post('DeleteFromUserLocationMapping', {
    where: `UserId = ${num(userId)} AND LocationID = ${num(locationId)}`,
  });

/*
 * Every mapping row pointing at a location, for ALL users.
 *
 * ⚠️ Used when a location is released back to the pool. A row left behind is
 * not inert: `GetLocationsByUserId` joins on UserLocationMapping, so the old
 * customer keeps seeing the location — and once it is claimed again they are
 * seeing the NEW customer's site. That is the leak this exists to prevent, and
 * it is the same shape as the phantom that made "unallot" look broken.
 */
/*
 * Everyone a device is mapped to, WITH their names. -> [{UserId, Name,
 * IsActive, IsMasterUser}]
 *
 * ⚠️ TAKES THE INSTALL ID, NOT THE UNIQUE NUMBER, and is the better of the two.
 * `GetAllUsersByUniqueNumber` answers the same question but returns bare ids,
 * so the caller has to hold all 460 users to turn them into names.
 *
 * ⚠️ AN EMPTY REPLY IS A REAL ANSWER. A device with no UserDeviceMapping rows
 * is visible to nobody — not even its own master, whose ownership lives on
 * DevicesInstalled.MasterUserId and is a separate fact. Probed: 801824 (install
 * 1320) is owned by AMIT and mapped to no one at all.
 */
export const getUsersForDevice = (deviceInstalledId) =>
  post('GetUsersForParticularDevicebyDeviceInstalledID', {
    DeviceInstalledID: Number(deviceInstalledId) || 0,
  });

export const deleteAllUserLocationMappings = (locationId) =>
  post('DeleteFromUserLocationMapping', {
    where: `LocationID = ${num(locationId)}`,
  });

export const deleteAllLocationRoomsMappings = (locationId) =>
  post('DeleteFromLocationRoomsMapping', {
    where: `LocationID = ${num(locationId)}`,
  });

export const deleteLocationRoomsMapping = (userId, locationId, roomId) =>
  post('DeleteFromLocationRoomsMapping', {
    where: `UserId = ${num(userId)} AND LocationID = ${num(locationId)}`
      + ` AND RoomID = ${num(roomId)}`,
  });

// ---------------- User registration ----------------
// linker.dart getRegistered() — field names and the ISD:91 literal are the
// Flutter payload exactly.
export const registerNewUser = ({
  name, middleName, lastName, password, email, mobile, deviceName,
}) =>
  post('AddNewUser', {
    Surname: lastName,
    Name: name,
    FatherHusband: middleName,
    Password: password,
    ISD: 91,
    MobileNumber: Number(mobile),
    EmailId: email,
    DeviceName: deviceName,
  });

// Bind this phone to the account (single-device login enforcement).
export const updateUserDeviceName = (userId, deviceName) =>
  post('UpdateUsers', { data: { DeviceName: deviceName }, where: `UserId=${userId}` });

// forgotPassFunctions.dart updateId(): a password reset ALSO re-binds the
// account to the phone doing the reset, which is what logs it out elsewhere.
export const resetPasswordAndBindDevice = (mobileNumber, password, deviceName) =>
  post('UpdateUsers', {
    data: { DeviceName: deviceName, Password: password },
    where: `MobileNumber=${mobileNumber}`,
  });

// Clears the server's "local cache is stale" flag after a successful sync.
export const clearSinkData = (userId) =>
  post('UpdateUsers', { data: { SinkData: '0' }, where: `UserId = ${userId}` });

export default api;
